import React, { useEffect, useMemo, useState } from "react";
import styles from "styles.module.css";
import { t } from "i18next";
import CustomButton from "ui/Button";
import { CustomInput } from "ui/Input";
import CustomDropDownList from "ui/dropDown";
import { getGetInternationalCountryCodeApi } from "api/common";
import { createNewUserApi } from "api/administration";
import { useNavigate } from "react-router";
import { PageTypePath } from "utils/pages";
import { COLOR } from "utils/constant";
import { useToastStore } from "store/toast";

type PhoneNumberState = {
	prefix: string;
	number: string;
};

type NewUserFormState = {
	email: string;
	firstName: string;
	lastName: string;
	phoneNumber: PhoneNumberState;
};

type NewUserFormErrors = Partial<
	Record<"email" | "firstName" | "lastName" | "phoneNumber", string>
>;

const DEFAULT_PHONE_PREFIX = "+47";

export default function NewUserForm() {
	const navigate = useNavigate();
	const { showSuccess } = useToastStore();
	const [countryCodes, setCountryCodes] = useState<string[]>([]);
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState<NewUserFormErrors>({});
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [form, setForm] = useState<NewUserFormState>({
		email: "",
		firstName: "",
		lastName: "",
		phoneNumber: {
			prefix: DEFAULT_PHONE_PREFIX,
			number: "",
		},
	});

	useEffect(() => {
		getGetInternationalCountryCodeApi()
			.then((response) => {
				setCountryCodes(response.map((code) => code.internationalDialing));
			})
			.catch(() => {
				setErrorMessage(t("Failed"));
			});
	}, []);

	const emailIsValid = (value: string) => {
		if (!value) return false;
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
	};

	const phoneIsValid = (value: string) => /^\d{8}$/.test(value);

	const validate = () => {
		const nextErrors: NewUserFormErrors = {};
		setErrorMessage("");

		if (!emailIsValid(form.email)) {
			nextErrors.email = t("Email");
		}
		if (!form.firstName.trim()) {
			nextErrors.firstName = t("FirstName");
		}
		if (!form.lastName.trim()) {
			nextErrors.lastName = t("LastName");
		}
		if (!phoneIsValid(form.phoneNumber.number)) {
			nextErrors.phoneNumber = t("PhoneNumber");
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handlePhoneNumberChange = (value: string) => {
		if ((value === "" || /^[0-9]*$/.test(value)) && value.length <= 8) {
			setForm((prev) => ({
				...prev,
				phoneNumber: { ...prev.phoneNumber, number: value },
			}));
		}
	};

	const handleSubmit = async () => {
		if (saving) return;
		if (!validate()) return;

		setSaving(true);
		setErrorMessage("");
		try {
			const response = await createNewUserApi({
				email: form.email.trim(),
				firstName: form.firstName.trim(),
				lastName: form.lastName.trim(),
				phoneNumber: {
					prefix: form.phoneNumber.prefix,
					number: form.phoneNumber.number,
				},
				roles: [],
			});

			const userId =
				response?.data?.id ||
				response?.data?.userId ||
				response?.data?.Id ||
				response?.data?.UserId;

			showSuccess(`${t("Save")} ${t("Success")}`, { duration: 2000 });

			if (userId) {
				navigate(`/${PageTypePath.UserInformation}/${userId}`);
				return;
			}
			navigate(`/${PageTypePath.Users}`);
		} catch (error: any) {
			if (error?.response?.status === 409 || error?.status === 409) {
				setErrorMessage(t("UserAlreadyExists"));
				return;
			}
			setErrorMessage(`${t("Save")} ${t("Failed")}`);
		} finally {
			setSaving(false);
		}
	};

	const submitDisabled = useMemo(() => saving, [saving]);

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("NewUserAgreement")}
				</legend>
			</div>
			{errorMessage && (
				<div style={{ color: COLOR.StatusRed }}>{errorMessage}</div>
			)}

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "20px",
				}}
			>
				<div style={{ display: "flex", gap: "20px" }}>
					<div style={{ width: "100%" }}>
						<CustomInput
							label={t("Email")}
							value={form.email}
							// type="email"
							autoComplete="email"
							isError={!!errors.email}
							onChange={(value) =>
								setForm((prev) => ({ ...prev, email: value }))
							}
						/>
						{errors.email && (
							<span style={{ color: COLOR.StatusRed }}>{t("Email")}</span>
						)}
					</div>
				</div>

				<div style={{ display: "flex", gap: "20px" }}>
					<div style={{ width: "100%" }}>
						<CustomInput
							label={t("FirstName")}
							value={form.firstName}
							isError={!!errors.firstName}
							autoComplete="given-name"
							onChange={(value) =>
								setForm((prev) => ({ ...prev, firstName: value }))
							}
						/>
						{errors.firstName && (
							<span style={{ color: COLOR.StatusRed }}>{t("FirstName")}</span>
						)}
					</div>
					<div style={{ width: "100%" }}>
						<CustomInput
							label={t("LastName")}
							value={form.lastName}
							autoComplete="family-name"
							isError={!!errors.lastName}
							onChange={(value) =>
								setForm((prev) => ({ ...prev, lastName: value }))
							}
						/>
						{errors.lastName && (
							<span style={{ color: COLOR.StatusRed }}>{t("LastName")}</span>
						)}
					</div>
				</div>

				<div style={{ display: "flex", gap: "20px" }}>
					<div style={{ width: "100%" }}>
						<div style={{ display: "flex", gap: "20px", width: "100%" }}>
							<CustomDropDownList
								style={{ width: "50%" }}
								data={countryCodes}
								dataItemKey=""
								textField=""
								label={t("CountryCode")}
								value={form.phoneNumber.prefix}
								onChange={(value) => {
									setForm((prev) => ({
										...prev,
										phoneNumber: {
											...prev.phoneNumber,
											prefix: value.value,
										},
									}));
								}}
							/>
							<CustomInput
								label={t("MobileNumber")}
								placeholder={t("MobileNumber")}
								value={form.phoneNumber.number || ""}
								autoComplete="tel-national"
								isError={!!errors.phoneNumber}
								onChange={handlePhoneNumberChange}
							/>
						</div>
						{errors.phoneNumber && (
							<span style={{ color: COLOR.StatusRed }}>{t("PhoneNumber")}</span>
						)}
					</div>
				</div>
			</div>

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					paddingTop: "30px",
				}}
			>
				<CustomButton
					label={t("Cancel")}
					onClick={() => navigate(`/${PageTypePath.Users}`)}
					bgColor={COLOR.Grey98}
				/>
				<CustomButton
					label={t("Save")}
					onClick={handleSubmit}
					disabled={submitDisabled}
					bgColor={COLOR.Green}
					labelStyle={{ color: "white" }}
				/>
			</div>
		</div>
	);
}
